import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModalAction } from '../../../actions/modal';
import moment from 'moment';
import { isValidGST } from '../../../utils/utils';
import IconToolTip from '../../../common/IconToolTip';
import DatePicker from '../../../common/Form/DatePicker';
import { useForm } from 'react-hook-form';
import Input from '../../../common/Form/Input';
import Form from '../../../common/Form';
import { motion } from 'framer-motion';
import { DateComponent } from '../../../common/TableReusableColumns';
import { validateDateValue, validateResponseDate } from '../../../utils/common';
import { createInvoice } from '../../../actions/payments';
import { SEND_TOAST } from '../../../types/toast';

const formName = 'generateInvoice';

const GenerateInvoice = ({ po }) => {
    const dispatch = useDispatch();
    const copyTxnDetailsLoading = useSelector((state) => state.loading.copyTxnDetailsLoading);
    const [activeCopyItemId, setActiveCopyItemId] = useState(undefined);
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const [selectedItems, setSelectedItems] = useState([]);

    useEffect(() => {
        if (!copyTxnDetailsLoading) {
            setActiveCopyItemId(undefined);
        }
        return () => {
            if (!copyTxnDetailsLoading && !['', undefined, null].includes(activeCopyItemId)) {
                setActiveCopyItemId(undefined);
            }
        };
    }, []);

    const initialValues = useMemo(
        () => ({
            poItemIds: [],
            customer_address: '',
            circuitId: '',
            customer_gst: '',
            invoice_date: '',
            invoice_no: '',
            sac_code: '',
            vendor_gst: '',
            customerPoNumber: '',
            supportingDocuments: []
        }),
    );

    const methods = useForm({ defaultValues: initialValues, mode: 'all' });

    const submit = (formValues) => {
        if (selectedItems.length === 0) {
            dispatch({ type: SEND_TOAST, payload: { status: 'warning', message: 'Select atleast one item' } });
            return;
        }
        const payload = {
            org_id: po.org_id,
            org_gst_number: formValues.org_gst_number,
            customer_id: po.org_id,
            customer_gst_number: formValues.vendor_gst_number,
            item: 'PO',
            currency: 'INR',
            invoice_number: formValues.invoice_number,
            invoice_date: formValues.invoice_date,
            external_po_number: formValues.external_po_number,
            is_recurring: false,
            description: formValues.description
        };

        payload.line_items = selectedItems.map((elem) => {
            const { item_id, item_title, quantity, unit_price } = po.line_items.find((item) => item.id === elem);
            return {
                item_type: "PO",
                item_id,
                item_title,
                quantity,
                unit_price,
                currency: "INR",
            }
        })

        dispatch(createInvoice({ payload, formName }));
    };

    const closeModal = () => {
        dispatch(closeModalAction());
    };

    return (
        <div style={{ heigth: '65vh' }}>
            <Form
                className='w-full'
                onSubmit={submit}
                onCancel={closeModal}
                methods={methods}
                showSubmit
                submitContainerClassName='flex gap-2 justify-end py-4 px-4'
                isLoading={formButtonLoading[formName]}
            >
                <div style={{ padding: '1.25rem' }}>
                    <div className="overflow-hidden border border-gray-300 rounded-lg">
                        <table className="min-w-full border-collapse">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Asset Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Category</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Description</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Recurring</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">End Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Quantity</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Total Price</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {po.line_items.map((line_item, idx) => (
                                    <motion.tr
                                        key={line_item.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => {
                                            if (selectedItems.includes(line_item.id)) {
                                                setSelectedItems((prevState) => prevState.filter((i) => i !== line_item.id));
                                            } else {
                                                setSelectedItems((prevState) => [line_item.id, ...prevState]);
                                            }
                                        }}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedItems.includes(line_item.id)}
                                                    onChange={() => { }}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-3 text-sm text-gray-900">{line_item.item_title}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{line_item.category}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{line_item.description}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {line_item.is_recurring ? 'Yes' : 'No'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {line_item.end_date ? <DateComponent date={validateDateValue(line_item.end_date) && moment(new Date(validateResponseDate(line_item.end_date)))} text={false} timeFormat='DD - MMM - YYYY' /> : <></>}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{line_item.quantity}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{line_item.total_price}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{line_item.payment_status}</td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className='px-3 grid grid-cols-3 rows-fr grid-flow-row-dense'>
                    <Input label='Invoice No' required name='invoice_number' type={'text'} />
                    <DatePicker label='Invoice Date' required name='invoice_date' dateFormat='dd-MM-yyyy' inputValueFormat={'DD-MM-yyyy'} />
                    {/* <Input label='Supporting Documents' name='supportingDocuments' multiple={true} type='upload' /> */}
                    <Input label='Customer PO' name='external_po_number' type={'text'} />
                    <Input label='GST' name='org_gst_number' type={'text'}
                        validate={(gst) => {
                            if (gst === '') return true;
                            return isValidGST(gst) || 'Invalid GST';
                        }}
                    />
                    <Input label='Address' name='customer_address' type={'text'} />
                    <Input label='Vendor GST' name='vendor_gst_number' type={'text'}
                        validate={(gst) => {
                            if (gst === '') return true;
                            return isValidGST(gst) || 'Invalid GST';
                        }}
                    />
                    <Input label='Description' name='description' type={'text'} className='col-span-3' />
                </div>
            </Form>
        </div>
    );
};

export default GenerateInvoice;